import { ReactNode } from "react";
import { Link } from "react-router";
import { Button, Result, Spin } from "antd";
import { leadGenError } from "@/services/leadGenApi";
import { useLeadGenMe, type LeadGenRole } from "@/services/leadGenService";

/** Guards a Lead Gen page by Lead Gen role (e.g. a typed URL to a page the menu hides). The server enforces it too. */
export default function LeadGenRoleGuard({ roles, children }: { roles: LeadGenRole[]; children: ReactNode }) {
  const { data: me, isLoading, error, refetch, isFetching } = useLeadGenMe();
  if (isLoading) return <Spin className="flex justify-center p-12" />;
  if (me?.role && roles.includes(me.role)) return <>{children}</>;
  // the role could not be loaded (server down, a failing request): say so instead of "no role"
  if (!me && error)
    return (
      <Result
        status="warning"
        title="Couldn't load your Lead Gen access"
        subTitle={`${leadGenError(error)} Check that the server is running, then try again.`}
        extra={
          <Button type="primary" loading={isFetching} onClick={() => void refetch()}>
            Try again
          </Button>
        }
      />
    );
  return (
    <Result
      status="403"
      title="No access"
      subTitle={me?.role ? "Your Lead Gen role can't open this page." : "You don't have a Lead Gen role. Ask an admin for one."}
      extra={
        me?.role ? (
          // a Collector has no dashboard: send them to Lead Sources
          <Link to={me.role === "collector" ? "/lead-gen/sources" : "/lead-gen/dashboard"}>
            <Button type="primary">{me.role === "collector" ? "Lead Sources" : "Lead Gen dashboard"}</Button>
          </Link>
        ) : null
      }
    />
  );
}
